import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { COPIED_FEEDBACK_MS } from '../constants';
import { useOemFormatContext } from '../hooks/useOemFormatContext';
import { formatPayload } from '../utils/formatPayload';
import '../oem-data-modal.scss';

const MetaItem = ({ label, children }) => (
	<div className="oem-json-meta-item">
		<dt>{label}</dt>
		<dd>{children}</dd>
	</div>
);

/**
 * The reading as stored: identity strip (manufacturer, event id, unit time,
 * event name) over the pretty-printed message, with a copy action. This is the
 * whole modal for manufacturers without an adapter.
 */
export const RawDataJsonView = ({ record }) => {
	const { t, formatDateTime } = useOemFormatContext(record.unitId);
	const [copied, setCopied] = useState(false);
	const timerRef = useRef(null);
	const text = useMemo(() => formatPayload(record.rawMessage), [record.rawMessage]);
	const na = t('oemDataModal_notAvailable');

	// Only cleans up the feedback timer on unmount.
	useEffect(() => () => clearTimeout(timerRef.current), []);

	const copy = () => {
		// Absent outside secure contexts; a denied write is not worth an error UI.
		const clipboard = navigator.clipboard;
		if (!clipboard) return;
		clipboard
			.writeText(text)
			.then(() => {
				setCopied(true);
				clearTimeout(timerRef.current);
				timerRef.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
			})
			.catch(() => {});
	};

	return (
		<div>
			<dl className="oem-json-meta">
				<MetaItem label={t('oemDataModal_meta_manufacturer')}>{record.manufacturerName || na}</MetaItem>
				<MetaItem label={t('oemDataModal_meta_eventId')}>{record.id}</MetaItem>
				<MetaItem label={t('oemDataModal_meta_unitTime')}>
					{(record.unitTime && formatDateTime(record.unitTime)) || na}
				</MetaItem>
				<MetaItem label={t('oemDataModal_meta_eventName')}>{record.eventName || na}</MetaItem>
				<Button variant="outlined" size="small" className="oem-json-copy" onClick={copy}>
					{t(copied ? 'oemDataModal_copied' : 'oemDataModal_copy')}
				</Button>
			</dl>
			<pre className="oem-json-pre">{text}</pre>
		</div>
	);
};
